import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import LeftNav from './components/LeftNav.tsx';
import HomeScreen from './components/HomeScreen.tsx';
import { useTheme } from './hooks/useTheme.ts';
import { useAuth } from './hooks/useAuth.ts';
import { useApi } from './hooks/useApi.ts';
import { NotificationProvider } from './context/NotificationContext.tsx';
import { PresenceProvider } from './context/PresenceContext.tsx';

// Route screens are lazy-loaded so the initial bundle only carries the landing
// screen (HomeScreen) + chrome. Everything else is fetched on navigation.
const PuzzlesScreen = lazy(() => import('./components/PuzzlesScreen.tsx'));
const PuzzlesHub = lazy(() => import('./components/PuzzlesHub.tsx'));
const EditorScreen = lazy(() => import('./components/EditorScreen.tsx'));
const HowToPlayScreen = lazy(() => import('./components/HowToPlayScreen.tsx'));
const LessonsHub = lazy(() => import('./components/LessonsHub.tsx'));
const SolveScreen = lazy(() => import('./components/SolveScreen.tsx'));
const EndlessScreen = lazy(() => import('./components/EndlessScreen.tsx'));
const SpeedrunHub = lazy(() => import('./components/SpeedrunHub.tsx'));
const SpeedrunGame = lazy(() => import('./components/SpeedrunGame.tsx'));
const AboutScreen = lazy(() => import('./components/AboutScreen.tsx'));
const ProfileScreen = lazy(() => import('./components/ProfileScreen.tsx'));
const ProfileEditScreen = lazy(() => import('./components/ProfileEditScreen.tsx'));
const CommunityHub = lazy(() => import('./components/CommunityHub.tsx'));
const AchievementsScreen = lazy(() => import('./components/AchievementsScreen.tsx'));
const GlobalLeaderboardScreen = lazy(() => import('./components/GlobalLeaderboardScreen.tsx'));
const ExportScreen = lazy(() => import('./components/ExportScreen.tsx'));
const LobbyScreen = lazy(() => import('./components/LobbyScreen.tsx'));
const LiveGameScreen = lazy(() => import('./components/LiveGameScreen.tsx'));
const SettingsModal = lazy(() => import('./components/SettingsModal.tsx'));
const WelcomeModal = lazy(() => import('./components/WelcomeModal.tsx'));
const ReconnectModal = lazy(() => import('./components/ReconnectModal.tsx'));

const RECONNECT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export default function App() {
  useTheme(); // applies body class on mount; toggling handled inside SettingsModal
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(
    () => !localStorage.getItem('rikoro:welcomed')
  );
  // Whether this device has ever completed a login (set on first auth, cleared
  // on explicit logout). Read once at mount — it's what decides if a returning,
  // currently-signed-out user should be offered a reconnect prompt.
  const hadSession = useRef(!!localStorage.getItem('rikoro:hadSession'));
  const [reconnectDismissed, setReconnectDismissed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, login } = useAuth();
  const { getMe } = useApi();
  const newUserChecked = useRef(false);

  // Record that a session existed once auth succeeds (no identity stored).
  useEffect(() => {
    if (isAuthenticated) localStorage.setItem('rikoro:hadSession', '1');
  }, [isAuthenticated]);

  const reconnectCooledDown =
    Date.now() - Number(localStorage.getItem('rikoro:reconnectDismissedAt') || 0) >
    RECONNECT_COOLDOWN_MS;
  // Don't interrupt deep-link/gameplay routes (e.g. a shared-puzzle visitor).
  const reconnectBlockedPath =
    location.pathname === '/profile/edit' ||
    location.pathname.startsWith('/puzzles/solve/');
  const reconnectOpen =
    !isLoading &&
    !isAuthenticated &&
    hadSession.current &&
    reconnectCooledDown &&
    !reconnectDismissed &&
    !reconnectBlockedPath;

  useEffect(() => {
    const path = location.pathname;
    const titles: [RegExp, string][] = [
      [/^\/puzzles\/solve\/shared/, 'Shared Puzzle · Rikoro'],
      [/^\/puzzles\/solve\//, 'Solve Puzzle · Rikoro'],
      [/^\/puzzles\/endless$/, 'Endless Mode · Rikoro'],
      [/^\/puzzles\/speedrun\/play$/, 'Speedrun · Rikoro'],
      [/^\/puzzles\/speedrun$/, 'Speedrun · Rikoro'],
      [/^\/puzzles\/editor$/, 'Puzzle Editor · Rikoro'],
      [/^\/puzzles\/browse$/, 'Browse Puzzles · Rikoro'],
      [/^\/puzzles$/, 'Puzzles · Rikoro'],
      [/^\/lessons\/how-to-play$/, 'How to Play · Rikoro'],
      [/^\/lessons$/, 'Lessons · Rikoro'],
      [/^\/achievements$/, 'Achievements · Rikoro'],
      [/^\/community\/leaderboard$/, 'Global Leaderboard · Rikoro'],
      [/^\/community$/, 'Community · Rikoro'],
      [/^\/multiplayer\//, 'Multiplayer Game · Rikoro'],
      [/^\/multiplayer$/, 'Multiplayer Lobby · Rikoro'],
      [/^\/profile\/edit$/, 'Edit Profile · Rikoro'],
      [/^\/profile\//, 'Player Profile · Rikoro'],
      [/^\/profile$/, 'My Profile · Rikoro'],
      [/^\/about$/, 'About · Rikoro'],
      [/^\/$/, 'Rikoro · Ricochet Robot Puzzle Game'],
    ];
    const match = titles.find(([re]) => re.test(path));
    document.title = match ? match[1] : 'Rikoro · Ricochet Robot Puzzle Game';

    const descriptions: [RegExp, string][] = [
      [/^\/puzzles\/solve\//, 'Someone shared a Ricochet Robot puzzle with you. Can you solve it? Slide robots to their target in as few moves as possible.'],
      [/^\/about$/, 'Rikoro is a free daily puzzle game based on Ricochet Robots by Alex Randolph. Slide robots to their target in as few moves as possible.'],
      [/^\/puzzles\/endless$/, 'Play Ricochet Robot puzzles endlessly. New random puzzles generated on the fly — how far can you go?'],
      [/^\/puzzles\/speedrun$/, 'Race against the clock in Rikoro Speedrun mode. Solve as many Ricochet Robot puzzles as you can in 3 minutes.'],
      [/^\/lessons\/how-to-play$/, 'Learn how to play Ricochet Robots. Slide robots across the board to reach the target in the fewest moves.'],
      [/^\/community\/leaderboard$/, 'See the top Rikoro players from around the world. Can you make it onto the global leaderboard?'],
      [/^\/$/, 'Play Ricochet Robot online. A daily sliding-robot puzzle game. Move robots to reach the target in the fewest moves.'],
    ];
    const descMatch = descriptions.find(([re]) => re.test(path));
    const desc = descMatch ? descMatch[1] : 'Play Ricochet Robot online. A daily sliding-robot puzzle game. Move robots to reach the target in the fewest moves.';
    document.querySelector('meta[name="description"]')?.setAttribute('content', desc);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', desc);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', desc);

    // Self-referential canonical per route. The static index.html hardcodes the
    // homepage as canonical on every page, which made Google consolidate every
    // route into "/" and drop them from the index. Keep "/" trailing-slashed to
    // match index.html / the sitemap; every other path is bare (no trailing slash).
    const canonicalPath = path === '/' ? '/' : path.replace(/\/$/, '');
    const canonicalUrl = `https://rikoro.com${canonicalPath}`;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonicalUrl);
  }, [location.pathname]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || newUserChecked.current) return;
    newUserChecked.current = true;
    getMe().then((profile) => {
      if (profile.country === 'UN' && !localStorage.getItem('rikoro:setupSeen')) {
        localStorage.setItem('rikoro:setupSeen', '1');
        navigate('/profile/edit');
      }
    }).catch(() => {});
  }, [isLoading, isAuthenticated, getMe, navigate]);

  return (
    <NotificationProvider>
    <PresenceProvider>
    <div className="app-root" style={{ display: 'flex', height: '100vh' }}>
      <LeftNav onOpenSettings={() => setSettingsOpen(true)} />
      <Suspense fallback={null}>
        {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
        {welcomeOpen && !hadSession.current && location.pathname !== '/profile/edit' && (
          <WelcomeModal onDismiss={() => {
            localStorage.setItem('rikoro:welcomed', '1');
            setWelcomeOpen(false);
          }} />
        )}
        {reconnectOpen && (
          <ReconnectModal
            onReconnect={login}
            onDismiss={() => {
              localStorage.setItem('rikoro:reconnectDismissedAt', String(Date.now()));
              setReconnectDismissed(true);
            }}
          />
        )}
      </Suspense>

      <main className="app-main" style={{
        flex: 1,
        position: 'relative',
        overflowY: 'auto',
      }}>
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/puzzles" element={<PuzzlesHub />} />
          <Route path="/puzzles/browse" element={<PuzzlesScreen />} />
          <Route path="/puzzles/solve/shared" element={<SolveScreen />} />
          <Route path="/puzzles/solve/:id" element={<SolveScreen />} />
          <Route path="/puzzles/endless" element={<EndlessScreen />} />
          <Route path="/puzzles/speedrun" element={<SpeedrunHub />} />
          <Route path="/puzzles/speedrun/play" element={<SpeedrunGame />} />
          <Route path="/puzzles/editor" element={<EditorScreen />} />
          <Route path="/editor" element={<Navigate to="/puzzles/editor" replace />} />
          <Route path="/lessons" element={<LessonsHub />} />
          <Route path="/lessons/how-to-play" element={<HowToPlayScreen />} />
          <Route path="/how-to-play" element={<Navigate to="/lessons/how-to-play" replace />} />
          <Route path="/about" element={<AboutScreen />} />
          <Route path="/profile" element={<ProfileScreen />} />
          <Route path="/profile/edit" element={<ProfileEditScreen />} />
          <Route path="/profile/:id" element={<ProfileScreen />} />
          <Route path="/achievements" element={<AchievementsScreen />} />
          <Route path="/community" element={<CommunityHub />} />
          <Route path="/community/leaderboard" element={<GlobalLeaderboardScreen />} />
          <Route path="/multiplayer" element={<LobbyScreen />} />
          <Route path="/multiplayer/:roomId" element={<LiveGameScreen />} />
          <Route path="/export" element={<ExportScreen />} />
          <Route path="/export/:id" element={<ExportScreen />} />
        </Routes>
        </Suspense>
      </main>
    </div>
    </PresenceProvider>
    </NotificationProvider>
  );
}
